import type { ActivityItem, ActivityRenderGroup } from "./render-model";

type ActivityReasoningGroup = {
  kind: "reasoning-group";
  partIndices: number[];
  state?: "streaming" | "done";
  stepIndex: number;
  text: string;
};

type ActivityToolCall = Extract<ActivityItem, { kind: "tool" }>;

export type ParallelToolBatch = {
  kind: "parallel-tool-batch";
  batchId: string;
  stepIndex: number;
  tools: ActivityToolCall[];
  totalCount: number;
  completedCount: number;
  runningCount: number;
  errorCount: number;
  deniedCount: number;
  hasApproval: boolean;
  state: "running" | "completed" | "completed-with-errors";
};

export type ActivityDisplayNode =
  | ActivityReasoningGroup
  | Exclude<ActivityItem, { kind: "reasoning" | "tool" }>
  | ActivityToolCall
  | ParallelToolBatch;

function createParallelToolBatch(tools: ActivityToolCall[]): ParallelToolBatch {
  const completedCount = tools.filter(
    (tool) => tool.part.state === "output-available",
  ).length;
  const errorCount = tools.filter(
    (tool) => tool.part.state === "output-error",
  ).length;
  const deniedCount = tools.filter(
    (tool) => tool.part.state === "output-denied",
  ).length;
  const runningCount = tools.length - completedCount - errorCount - deniedCount;

  return {
    kind: "parallel-tool-batch",
    batchId: tools[0]?.part.toolCallId ?? "parallel-tool-batch",
    stepIndex: tools[0]?.stepIndex ?? 0,
    tools,
    totalCount: tools.length,
    completedCount,
    runningCount,
    errorCount,
    deniedCount,
    hasApproval: tools.some((tool) =>
      ["approval-requested", "approval-responded"].includes(tool.part.state),
    ),
    state:
      runningCount > 0
        ? "running"
        : errorCount > 0 || deniedCount > 0
          ? "completed-with-errors"
          : "completed",
  };
}

/**
 * 同一 step 内で連続する reasoning と tool call を、表示上の group / batch へ投影する。
 * transport と保存データは変更せず、reasoning・中間説明・step の境界は越えない。
 */
export function groupActivityItemsForDisplay(
  group: ActivityRenderGroup,
): ActivityDisplayNode[] {
  const displayItems: ActivityDisplayNode[] = [];

  for (const step of group.steps) {
    let pendingTools: ActivityToolCall[] = [];
    const flushPendingTools = () => {
      if (pendingTools.length === 1 && pendingTools[0]) {
        displayItems.push(pendingTools[0]);
      } else if (pendingTools.length > 1) {
        displayItems.push(createParallelToolBatch(pendingTools));
      }
      pendingTools = [];
    };

    for (const item of step.items) {
      if (item.kind === "tool") {
        pendingTools.push(item);
        continue;
      }

      flushPendingTools();

      if (item.kind !== "reasoning") {
        displayItems.push(item);
        continue;
      }

      const previousItem = displayItems.at(-1);
      if (
        previousItem?.kind === "reasoning-group" &&
        previousItem.stepIndex === item.stepIndex
      ) {
        previousItem.partIndices.push(item.partIndex);
        previousItem.text = `${previousItem.text}\n\n${item.part.text}`;
        previousItem.state = item.part.state;
        continue;
      }

      displayItems.push({
        kind: "reasoning-group",
        partIndices: [item.partIndex],
        state: item.part.state,
        stepIndex: item.stepIndex,
        text: item.part.text,
      });
    }

    flushPendingTools();
  }

  return displayItems;
}
